import type {
  CottageContactDealPageCreateForm,
  CottageContactDealPageCreateResponse,
} from '../entities/CottageContactDealPageCreate'

import type {
  CottageContactDealPagePricesDates,
  CottageContactDealPagePricesPeople,
} from '../entities/CottageContactDealPagePrices'

import type { CottageContactDealPage } from '../entities/CottageContactDealPage'
import fetchEmailContact from '~~/modules/api/rurality/v3/contacts/new'
import { getDataMining } from '~~/modules/integrations/datamining/composables/useDataMiningComposable'
import { type paramsType } from '~~/modules/api/rurality/v3/contacts/new/paramsSchema'

type responseType = {
  apiResponseSuccess: CottageContactDealPageCreateResponse | null
  apiResponseError: ApiResponseError | null
}

export function useCottageContactDealPage() {
  const format = (date: Date) => {
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  async function sendFormData(params: {
    createForm: CottageContactDealPageCreateForm
    cottageContactDealPage: CottageContactDealPage
    dates: CottageContactDealPagePricesDates
    people: CottageContactDealPagePricesPeople
  }): Promise<responseType> {
    const dataMining = getDataMining()

    const responseCottagePage: responseType = {
      apiResponseSuccess: null,
      apiResponseError: null,
    }

    const dealRentUnit = params.cottageContactDealPage.deal.rentUnitName
    const dealTitle = params.cottageContactDealPage.deal.title
    const stringTitle = t(
      'views.cottage-contact-deal.form.finalMessage.title',
      {
        dealRentUnit,
        dealTitle,
      },
    )

    const dealNights = params.cottageContactDealPage.deal.nights
    const stringNights = t(
      'views.cottage-contact-deal.form.finalMessage.nights',
      dealNights,
    )

    const dateStart = format(params.dates.start as Date)
    const dateEnd = format(params.dates.end as Date)
    const stringDates = t(
      'views.cottage-contact-deal.form.finalMessage.dates',
      {
        dateStart,
        dateEnd,
      },
    )

    const people = params.people
    const stringPeople = t(
      'views.cottage-contact-deal.form.finalMessage.people',
      people,
    )

    const dealOldTotal = toEuro(
      params.cottageContactDealPage.deal.totalOld.amount,
    )
    const dealTotal = toEuro(params.cottageContactDealPage.deal.total.amount)
    const stringTotal = t(
      'views.cottage-contact-deal.form.finalMessage.total',
      {
        dealOldTotal,
        dealTotal,
      },
    )

    const dealIncludes = params.cottageContactDealPage.deal.whatIncludes
      .replace(/(\r\n|\n|\r)/gm, '')
      .trim()

    const stringIncludes = t(
      'views.cottage-contact-deal.form.finalMessage.includes',
      {
        dealIncludes,
      },
    )

    const userMessage = params.createForm.message
    const stringMessage = t(
      'views.cottage-contact-deal.form.finalMessage.message',
      {
        userMessage,
      },
    )

    const finalMessage = `${stringTitle} ${stringNights}: ${stringDates}, ${stringPeople}, ${stringTotal}. ${stringIncludes} - ${stringMessage}`

    const queryParams: paramsType = {
      source: 'ESCAPADA_RURAL',
      cottageId: params.cottageContactDealPage.id,
      traveler: {
        name: params.createForm.name,
        email: params.createForm.email,
        phone: params.createForm.phone,
        allowComms: params.createForm.allowComms,
      },
      message: finalMessage,
      kpisData: dataMining,
      channel: 'EMAIL',
    }

    const contactEmailResponse = await fetchEmailContact(queryParams)

    if (contactEmailResponse?.success) {
      const cottageContactDealPageCreate: CottageContactDealPageCreateResponse =
        {
          id: contactEmailResponse.success.data.id,
          channel: contactEmailResponse.success.data.channel,
          message: contactEmailResponse.success.data.message,
          createdAt: contactEmailResponse.success.data.createdAt,
          owner: {
            id: contactEmailResponse.success.data.ownerId,
          },
          traveler: {
            id: contactEmailResponse.success.data.traveler.id,
            name: contactEmailResponse.success.data.traveler.name,
            email: contactEmailResponse.success.data.traveler.email,
            phone: contactEmailResponse.success.data.traveler.phone,
          },
          cottage: {
            id: contactEmailResponse.success.data.cottageId,
          },
        }
      responseCottagePage.apiResponseSuccess = cottageContactDealPageCreate
    } else if (contactEmailResponse.error) {
      responseCottagePage.apiResponseError =
        contactEmailResponse.error as ApiResponseError
    }

    return responseCottagePage
  }

  return {
    sendFormData,
  }
}
